// Owns the MediaPipe FaceLandmarker: loads the self-hosted WASM + model, runs
// per-frame detection on the camera <video>, and maps raw output to observations.

import { FilesetResolver, FaceLandmarker, type FaceLandmarkerResult } from "@mediapipe/tasks-vision";
import { CONFIG } from "../config";
import { buildObservation } from "./gaze";
import type { FaceObservation } from "../types";

export class VisionEngine {
  private landmarker: FaceLandmarker | null = null;
  private lastTimestamp = -1;
  public delegate: "GPU" | "CPU" = "CPU";

  async init(): Promise<void> {
    const fileset = await FilesetResolver.forVisionTasks(CONFIG.vision.wasmPath);

    const build = (delegate: "GPU" | "CPU") =>
      FaceLandmarker.createFromOptions(fileset, {
        baseOptions: {
          modelAssetPath: CONFIG.vision.modelAssetPath,
          delegate,
        },
        runningMode: "VIDEO",
        numFaces: CONFIG.vision.maxFaces,
        outputFaceBlendshapes: true,
        outputFacialTransformationMatrixes: true,
      });

    if (CONFIG.vision.preferGpu) {
      try {
        this.landmarker = await build("GPU");
        this.delegate = "GPU";
        return;
      } catch (err) {
        console.warn("[vision] GPU delegate failed, falling back to CPU:", err);
      }
    }
    this.landmarker = await build("CPU");
    this.delegate = "CPU";
  }

  /**
   * Detect faces in the current video frame. `timestampMs` must strictly
   * increase between calls; frames with a stale timestamp are skipped.
   */
  detect(video: HTMLVideoElement, timestampMs: number): FaceObservation[] {
    if (!this.landmarker) return [];
    if (timestampMs <= this.lastTimestamp) return [];
    this.lastTimestamp = timestampMs;

    let result: FaceLandmarkerResult;
    try {
      result = this.landmarker.detectForVideo(video, timestampMs);
    } catch (err) {
      console.warn("[vision] detectForVideo failed:", err);
      return [];
    }

    const faces = result.faceLandmarks ?? [];
    const blends = result.faceBlendshapes ?? [];
    const matrices = result.facialTransformationMatrixes ?? [];

    const observations: FaceObservation[] = [];
    for (let i = 0; i < faces.length; i++) {
      const landmarks = faces[i];
      if (!landmarks) continue;
      const categories = blends[i]?.categories ?? [];
      const matrixData = matrices[i]?.data;
      observations.push(buildObservation(landmarks, categories, matrixData));
    }
    return observations;
  }

  close(): void {
    this.landmarker?.close();
    this.landmarker = null;
  }
}
